import { useState } from 'react';
import { DndContext, DragEndEvent, DragStartEvent, DragOverlay } from '@dnd-kit/core';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { properties, type Property } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { toast } from '@/hooks/use-toast';
import { KanbanColumn } from './KanbanColumn';
import { PropertyCard } from './PropertyCard';
import type { KanbanProperty } from './types';

interface KanbanBoardProps {
  agencyId: string;
}

// Stage definitions with colors and labels
const STAGES = [
  {
    id: 'undirbúningur',
    label: 'Undir undirbúning',
    color: '#FEF3C7' // Yellow
  },
  {
    id: 'virk_sala',
    label: 'Virk sala',
    color: '#DBEAFE' // Blue
  },
  {
    id: 'tilboð_móttekin',
    label: 'Tilboð móttekin',
    color: '#E9D5FF' // Purple
  },
  {
    id: 'kaupsamningur',
    label: 'Kaupsamningur',
    color: '#D1FAE5' // Green
  },
  {
    id: 'þinglýsing',
    label: 'Þinglýsing',
    color: '#FED7AA' // Orange
  },
  {
    id: 'lokið',
    label: 'Lokið',
    color: '#F3F4F6' // Gray
  },
  {
    id: 'vandamál',
    label: 'Vandamál/Stöðvað',
    color: '#FEE2E2' // Red
  },
] as const;

export function KanbanBoard({ agencyId }: KanbanBoardProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const token = useAuthStore((state) => state.token);
  const queryClient = useQueryClient();

  // Fetch properties
  const { data, isLoading, error } = useQuery<KanbanProperty[]>({
    queryKey: ['properties', agencyId],
    queryFn: async () => {
      if (!token) throw new Error('No auth token');
      const response = await properties.list(token, { limit: 1000 });
      return response.data as KanbanProperty[];
    },
    enabled: !!token,
  });

  // Update property stage mutation
  const updateStageMutation = useMutation({
    mutationFn: async ({ propertyId, newStage }: { propertyId: string; newStage: string }) => {
      if (!token) throw new Error('No auth token');
      return properties.update(token, propertyId, { stage: newStage } as Partial<Property>);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties', agencyId] });
      toast({
        title: 'Árangur',
        description: 'Eign uppfærð',
        variant: 'default',
      });
    },
    onError: (error) => {
      console.error('Failed to update property stage:', error);
      toast({
        title: 'Villa',
        description: 'Ekki tókst að uppfæra eign',
        variant: 'destructive',
      });
    },
  });

  // Group properties by stage
  const propertiesByStage = (data || []).reduce<Record<string, KanbanProperty[]>>((acc, property) => {
    const stage = property.stage || 'undirbúningur';
    if (!acc[stage]) {
      acc[stage] = [];
    }
    acc[stage].push(property);
    return acc;
  }, {});

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      setActiveId(null);
      return;
    }

    const propertyId = active.id as string;
    const newStage = over.id as string;

    // Find the property being moved
    const property = data?.find(p => p.id === propertyId);
    if (!property) {
      setActiveId(null);
      return;
    }

    const currentStage = property.stage || 'undirbúningur';

    // Only update if stage actually changed
    if (currentStage !== newStage) {
      // Optimistic update
      queryClient.setQueryData(['properties', agencyId], (old: KanbanProperty[] | undefined) => {
        if (!old) return old;
        return old.map(p =>
          p.id === propertyId
            ? { ...p, stage: newStage }
            : p
        );
      });

      // Perform the update
      updateStageMutation.mutate({ propertyId, newStage });
    }

    setActiveId(null);
  };

  const handleCardClick = (_property: KanbanProperty) => {
    // TODO: Open property details modal
  };

  // Get the active property for drag overlay
  const activeProperty = activeId ? data?.find(p => p.id === activeId) : null;

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600">Villa við að sækja eignir</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-500">Hleð...</p>
      </div>
    );
  }

  return (
    <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="flex gap-4 overflow-x-auto pb-4">
        {STAGES.map((stage) => (
          <KanbanColumn
            key={stage.id}
            stage={stage.id}
            title={stage.label}
            color={stage.color}
            properties={propertiesByStage[stage.id] || []}
            onCardClick={handleCardClick}
          />
        ))}
      </div>

      <DragOverlay>
        {activeProperty ? (
          <div className="opacity-50 rotate-3">
            <PropertyCard property={activeProperty} onClick={() => {}} />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
